/**
 * vendor
 */
import React, { useState, useEffect, useRef, useContext } from 'react';

import EVENT_NAMES from '@shared/lib/constants/events';
import Emitter     from '@shared/services/main/events';
import { isFalsy } from '@shared/lib/helpers/common';

/**
 * PALTFORM MODULE
 */
import { default as PrepaidSessionService } from '@shared/services/session/services/prepaid/SessionService';
import { PlatformSessionContext }           from '@shared/services/session/stores/platform-session-store';

function SessionLoad() {
  const context = useContext(PlatformSessionContext);
  const [item, setItem] = useState(null);
  const serviceRef = useRef(null);
  const componentLikeRef = useRef(null);
  const prevItemRef = useRef(null);

  const readProceed = () => {
    // Only proceed if user has prepaid data in context
    // This prevents 401 errors for non-prepaid users
    // Check platform session context for prepaid indicators
    const [{ player, wallet, prepaid_player_id, prepaid_wallet_id }] = context;
    
    // Check if user has prepaid data in context
    // Prepaid data may come from:
    // 1. Previous prepaid session load (refresh scenario)
    // 2. Login response session data (if included)
    const hasPrepaidInContext = prepaid_player_id || prepaid_wallet_id || 
                                 player?.prepaid_player_id || wallet?.prepaid_wallet_id;
    
    // If no prepaid data in context, skip the call
    // This prevents unnecessary 401 errors for regular (non-prepaid) users
    // Prepaid users will have their data loaded through other mechanisms
    // or can trigger it manually when needed
    if (!hasPrepaidInContext) {
      return;
    }
    
    Emitter.emit(EVENT_NAMES.PLATFORM.SESSIONS_PREPAID.READ_PROCEED, {});
  };

  const syncData = () => {
    const [{ player }, dispatch] = context;
    const { prepaid_player_id: old_prepaid_player_id = null } = player;
    const {
      prepaid_player = null,
      prepaid_wallet = null,
    } = item;
    const {
      prepaid_player_id: new_prepaid_player_id = null
    } = prepaid_player || {};

    /**
     * If the player id has changed then we sync player and wallet again to the
     * context
     */
    if (old_prepaid_player_id != new_prepaid_player_id) {
      dispatch({type: 'SET_PLAYER', payload: prepaid_player});
      dispatch({type: 'SET_WALLET', payload: prepaid_wallet});
      dispatch({type: 'SET_BALANCE', payload: prepaid_wallet.balance});
    }
  };

  useEffect(() => {
    // Create component-like object for service
    const componentLike = {
      setState: (updates) => {
        if (typeof updates === 'function') {
          setItem(updates);
        } else {
          if (updates.item !== undefined) {
            setItem(updates.item);
          }
        }
      },
      state: { item },
      context
    };

    componentLikeRef.current = componentLike;
    serviceRef.current = new PrepaidSessionService(componentLike);

    serviceRef.current.registerReadEvents(componentLike, serviceRef.current);
    
    const readProceedHandler = readProceed;

    Emitter.register(
      EVENT_NAMES.PLATFORM.SESSIONS.START_SUCCESS_AFTER + '-prepaid-session-service',
      readProceedHandler
    );
    Emitter.register(
      EVENT_NAMES.LOCK.LOGIN_SUCCESS_AFTER + '-prepaid-session-service',
      readProceedHandler
    );

    return () => {
      if (serviceRef.current) {
        serviceRef.current.removeReadEvents();
      }
      Emitter.off(EVENT_NAMES.PLATFORM.SESSIONS.START_SUCCESS_AFTER + '-prepaid-session-service');
      Emitter.off(EVENT_NAMES.LOCK.LOGIN_SUCCESS_AFTER + '-prepaid-session-service');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update componentLike state when item changes
  useEffect(() => {
    if (componentLikeRef.current) {
      componentLikeRef.current.state = { item };
    }
  }, [item]);

  // Sync data when item changes
  useEffect(() => {
    if (!isFalsy(prevItemRef.current) && !isFalsy(item)) {
      syncData();
    }
    prevItemRef.current = item;
  }, [item]);

  return (<></>);
}

export default SessionLoad;
